import { useState, useMemo } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard, hexToRgb, rgbToHex, rgbToHsl, hslToRgb } from '@/lib/utils';
import { toast } from 'sonner';

type Mode =
  | 'monochromatic'
  | 'analogous'
  | 'complementary'
  | 'triadic'
  | 'tetradic'
  | 'shades'
  | 'tints';

function gen(base: string, mode: Mode, count: number): string[] {
  const rgb = hexToRgb(base);
  if (!rgb) return [];
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const out: { h: number; s: number; l: number }[] = [];

  if (mode === 'monochromatic') {
    for (let i = 0; i < count; i++) {
      const l = Math.round((90 - i * (70 / Math.max(1, count - 1))));
      out.push({ h: hsl.h, s: hsl.s, l });
    }
  } else if (mode === 'analogous') {
    const span = 60;
    for (let i = 0; i < count; i++) {
      const offset = -span / 2 + (span * i) / Math.max(1, count - 1);
      out.push({ h: (hsl.h + offset + 360) % 360, s: hsl.s, l: hsl.l });
    }
  } else if (mode === 'complementary') {
    out.push(hsl, { ...hsl, h: (hsl.h + 180) % 360 });
    while (out.length < count) {
      const last = out[out.length - 1];
      out.push({ ...last, l: Math.max(15, Math.min(85, last.l + 15)) });
    }
  } else if (mode === 'triadic') {
    [0, 120, 240].forEach((d) => out.push({ ...hsl, h: (hsl.h + d) % 360 }));
    while (out.length < count) out.push({ ...out[out.length - 1], l: Math.max(15, out[out.length - 1].l - 10) });
  } else if (mode === 'tetradic') {
    [0, 90, 180, 270].forEach((d) => out.push({ ...hsl, h: (hsl.h + d) % 360 }));
    while (out.length < count) out.push({ ...out[out.length - 1], l: Math.max(15, out[out.length - 1].l - 10) });
  } else if (mode === 'shades') {
    for (let i = 0; i < count; i++) {
      out.push({ ...hsl, l: Math.round(hsl.l - (hsl.l * i) / Math.max(1, count - 1)) });
    }
  } else if (mode === 'tints') {
    for (let i = 0; i < count; i++) {
      out.push({ ...hsl, l: Math.round(hsl.l + (95 - hsl.l) * (i / Math.max(1, count - 1))) });
    }
  }

  return out.slice(0, count).map((c) => {
    const r = hslToRgb(c.h, c.s, c.l);
    return rgbToHex(r.r, r.g, r.b);
  });
}

export function PaletteGenerator() {
  const [base, setBase] = useState('#6366f1');
  const [mode, setMode] = useState<Mode>('analogous');
  const [count, setCount] = useState(6);

  const palette = useMemo(() => gen(base, mode, count), [base, mode, count]);

  return (
    <div>
      <ToolHeader
        toolId="palette-generator"
        title="Palette generator"
        description="Build harmonized palettes — monochromatic, analogous, complementary, triadic, tetradic, shades, or tints."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Base color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={base}
                onChange={(e) => setBase(e.target.value)}
                className="!w-12 !h-9"
              />
              <Input
                value={base}
                onChange={(e) => setBase(e.target.value)}
                className="font-mono"
              />
            </div>
          </Field>

          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
            <TabsList className="w-full flex-wrap !h-auto !gap-1">
              <TabsTrigger value="monochromatic">Mono</TabsTrigger>
              <TabsTrigger value="analogous">Analogous</TabsTrigger>
              <TabsTrigger value="complementary">Complement</TabsTrigger>
              <TabsTrigger value="triadic">Triadic</TabsTrigger>
              <TabsTrigger value="tetradic">Tetradic</TabsTrigger>
              <TabsTrigger value="shades">Shades</TabsTrigger>
              <TabsTrigger value="tints">Tints</TabsTrigger>
            </TabsList>
          </Tabs>

          <Field label={`Colors (${count})`}>
            <input
              type="range"
              min={3}
              max={10}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
            />
          </Field>

          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => {
              const r = Math.floor(Math.random() * 0xffffff)
                .toString(16)
                .padStart(6, '0');
              setBase('#' + r);
            }}
          >
            <RefreshCw size={11} />
            Random base
          </Button>
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border min-h-[420px]">
            {palette.map((c) => (
              <Swatch key={c} hex={c} />
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-medium text-fg-muted">Export</div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const ok = await copyToClipboard(palette.join(', '));
                if (ok) toast.success('Hex list copied');
              }}
            >
              <Copy size={11} />
              Hex
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const css = palette
                  .map((c, i) => `--color-${i + 1}: ${c};`)
                  .join('\n');
                const ok = await copyToClipboard(css);
                if (ok) toast.success('CSS variables copied');
              }}
            >
              <Copy size={11} />
              CSS
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const tw = palette
                  .reduce<Record<string, string>>((acc, c, i) => {
                    acc[(i + 1) * 100] = c;
                    return acc;
                  }, {});
                const json = JSON.stringify({ brand: tw }, null, 2);
                const ok = await copyToClipboard(json);
                if (ok) toast.success('Tailwind JSON copied');
              }}
            >
              <Copy size={11} />
              Tailwind
            </Button>
          </div>
        </div>
        <code className="block p-3 rounded-md bg-bg-inset text-xs font-mono break-all">
          {palette.join(', ')}
        </code>
      </Card>
    </div>
  );
}

function Swatch({ hex }: { hex: string }) {
  const rgb = hexToRgb(hex);
  const yiq = rgb ? (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 : 0;
  const dark = yiq < 128;
  return (
    <button
      onClick={async () => {
        const ok = await copyToClipboard(hex);
        if (ok) toast.success(`${hex} copied`);
      }}
      className="flex flex-col items-start justify-end h-44 p-4 group transition-transform hover:scale-[1.02]"
      style={{ backgroundColor: hex }}
    >
      <div className={`text-sm font-mono font-medium ${dark ? 'text-white' : 'text-black'}`}>
        {hex.toUpperCase()}
      </div>
      <div
        className={`text-2xs font-mono mt-0.5 opacity-70 ${dark ? 'text-white' : 'text-black'}`}
      >
        rgb({rgb?.r}, {rgb?.g}, {rgb?.b})
      </div>
    </button>
  );
}

export default PaletteGenerator;
